import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

import {
  listReadings,
  getReadingStats,
  deleteReading,
  type ReadingRecord,
  type ReadingStats,
} from '@/api/client';
import OrnateFrame from '@/components/OrnateFrame';
import RichText from '@/components/RichText';
import PageNav from '@/components/PageNav';

const KIND_ICON: Record<string, string> = {
  daily: '☽',
  celtic_cross: '✦',
  three_card: '⁂',
  love: '♡',
  master: '◈',
  compatibility: '⊕',
  // Added with the spreads-pack + extras. Stick to single glyphs so
  // they line up with the existing visual grammar.
  dream: '☾',
  numerology: '🔢',
  themed: '🎯',
  weekly: '✧',
  journal: '✎',
  journal_insight: '✦',
  horseshoe: '🃏',
  magus_star: '🔮',
  wedding: '💍',
  home: '🏠',
  masks: '🎭',
  wallet: '💸',
  shadow: '🌑',
  crossroads: '🚪',
};

const KIND_FILTERS = [
  'daily', 'celtic_cross', 'three_card', 'love', 'master', 'compatibility',
  'dream', 'themed', 'numerology', 'weekly', 'journal',
  'horseshoe', 'magus_star', 'wedding', 'home', 'masks', 'wallet',
  'shadow', 'crossroads',
] as const;
type Tab = 'journal' | 'stats';

export default function Diary() {
  const { t, i18n } = useTranslation();
  const [readings, setReadings] = useState<ReadingRecord[]>([]);
  const [stats, setStats] = useState<ReadingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [tab, setTab] = useState<Tab>('journal');
  const [search, setSearch] = useState('');
  const [activeKind, setActiveKind] = useState<string | null>(null);
  // Date range filter — ISO strings "YYYY-MM-DD" or '' meaning open-ended.
  // Both empty = no range filter applied. Inclusive on both ends.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    setDeleting(true);
    setDeleteErr(null);
    try {
      await deleteReading(id);
      setReadings((rs) => rs.filter((r) => r.id !== id));
      setConfirmDelete(null);
    } catch {
      setDeleteErr(t('diary.delete_error'));
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    Promise.all([listReadings(100), getReadingStats()])
      .then(([r, s]) => { setReadings(r); setStats(s); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  const kindLabel = (kind: string) => {
    const key = `diary.kind_${kind}` as any;
    return t(key, { defaultValue: kind });
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(i18n.language.startsWith('ru') ? 'ru-RU' : 'en-GB', {
      day: 'numeric', month: 'long',
    });
  };

  // Apply search + kind + date-range filter.
  // Date range is inclusive on both ends. We compare against `for_date`,
  // falling back to the calendar-day prefix of `created_at` so legacy
  // rows without `for_date` still respect the filter.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return readings.filter((r) => {
      if (activeKind && r.kind !== activeKind) return false;
      if (dateFrom || dateTo) {
        const day = r.for_date ?? r.created_at?.slice(0, 10) ?? '';
        if (!day) return false;
        if (dateFrom && day < dateFrom) return false;
        if (dateTo && day > dateTo) return false;
      }
      if (!q) return true;
      return (r.interpretation?.toLowerCase().includes(q) ?? false)
          || (r.focus_question?.toLowerCase().includes(q) ?? false);
    });
  }, [readings, search, activeKind, dateFrom, dateTo]);

  // Quick-range presets — set both ends in one click.
  const setQuickRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    setDateFrom(iso(start));
    setDateTo(iso(end));
  };
  const clearRange = () => { setDateFrom(''); setDateTo(''); };
  const hasRangeFilter = !!(dateFrom || dateTo);

  // Group by calendar date
  const grouped = useMemo(() => {
    const acc: Record<string, ReadingRecord[]> = {};
    for (const r of filtered) {
      const day = r.for_date ?? r.created_at?.slice(0, 10) ?? '?';
      (acc[day] ??= []).push(r);
    }
    return acc;
  }, [filtered]);

  const days = useMemo(
    () => Object.keys(grouped).sort((a, b) => b.localeCompare(a)),
    [grouped],
  );

  return (
    <main className="mx-auto max-w-md px-5 pb-24 pt-2">
      <h1 className="mb-4 text-center font-serif text-2xl text-gold-300">
        {t('diary.title')}
      </h1>

      {/* Tabs */}
      <div className="mb-5 flex gap-2 rounded-full border border-gold-500/20 bg-night-900/50 p-1">
        {(['journal', 'stats'] as Tab[]).map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`flex-1 rounded-full px-3 py-2 text-xs uppercase tracking-widest transition
              ${tab === id
                ? 'bg-gold-500/20 text-gold-200'
                : 'text-gold-300/50 hover:text-gold-300'}`}
          >
            {t(id === 'journal' ? 'diary.tab_journal' : 'diary.tab_stats')}
          </button>
        ))}
      </div>

      {loading && (
        <p className="animate-pulse text-center text-gold-300/50">{t('reading.loading')}</p>
      )}
      {error && <p className="text-center text-xs text-rose-400">{error}</p>}

      {/* ----- JOURNAL TAB ----- */}
      {tab === 'journal' && !loading && (
        <>
          {/* Search */}
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('diary.search_placeholder')}
            className="mb-3 w-full rounded-xl border border-gold-500/25 bg-night-900/50
                       px-4 py-2.5 font-serif text-base text-gold-100 outline-none
                       transition placeholder:text-gold-300/35 focus:border-gold-400"
          />

          {/* Date range filter — two native date inputs + quick presets */}
          <div className="mb-3 space-y-2">
            <div className="flex items-center gap-2">
              <label className="flex flex-1 items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-widest text-gold-500/60">
                  {t('diary.range_from')}
                </span>
                <input
                  type="date"
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="flex-1 rounded-lg border border-gold-500/25 bg-night-900/50
                             px-2 py-1.5 text-xs text-gold-100 outline-none
                             transition focus:border-gold-400"
                />
              </label>
              <label className="flex flex-1 items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-widest text-gold-500/60">
                  {t('diary.range_to')}
                </span>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="flex-1 rounded-lg border border-gold-500/25 bg-night-900/50
                             px-2 py-1.5 text-xs text-gold-100 outline-none
                             transition focus:border-gold-400"
                />
              </label>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setQuickRange(0)}
                className="rounded-full border border-gold-500/25 px-2.5 py-1 text-[10px]
                           uppercase tracking-widest text-gold-300/70 transition
                           hover:border-gold-400/60"
              >
                {t('diary.range_today')}
              </button>
              <button
                type="button"
                onClick={() => setQuickRange(7)}
                className="rounded-full border border-gold-500/25 px-2.5 py-1 text-[10px]
                           uppercase tracking-widest text-gold-300/70 transition
                           hover:border-gold-400/60"
              >
                {t('diary.range_week')}
              </button>
              <button
                type="button"
                onClick={() => setQuickRange(30)}
                className="rounded-full border border-gold-500/25 px-2.5 py-1 text-[10px]
                           uppercase tracking-widest text-gold-300/70 transition
                           hover:border-gold-400/60"
              >
                {t('diary.range_month')}
              </button>
              {hasRangeFilter && (
                <button
                  type="button"
                  onClick={clearRange}
                  className="rounded-full border border-rose-400/40 px-2.5 py-1 text-[10px]
                             uppercase tracking-widest text-rose-300/80 transition
                             hover:border-rose-400"
                >
                  ✕ {t('diary.range_clear')}
                </button>
              )}
            </div>
          </div>

          {/* Kind filter chips */}
          <div className="mb-5 flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setActiveKind(null)}
              className={`rounded-full px-3 py-1 text-[11px] uppercase tracking-widest transition
                ${activeKind === null
                  ? 'border border-gold-500 bg-gold-500/20 text-gold-200'
                  : 'border border-gold-500/25 text-gold-300/70 hover:border-gold-400/60'}`}
            >
              {t('diary.filter_all')}
            </button>
            {KIND_FILTERS.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setActiveKind(activeKind === k ? null : k)}
                className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] uppercase tracking-widest transition
                  ${activeKind === k
                    ? 'border border-gold-500 bg-gold-500/20 text-gold-200'
                    : 'border border-gold-500/25 text-gold-300/70 hover:border-gold-400/60'}`}
              >
                <span>{KIND_ICON[k]}</span>
                <span>{kindLabel(k)}</span>
              </button>
            ))}
          </div>

          {readings.length === 0 && (
            <OrnateFrame className="card-glow text-center">
              <p className="font-serif italic text-gold-300/60">{t('diary.empty')}</p>
            </OrnateFrame>
          )}

          {readings.length > 0 && filtered.length === 0 && (
            <p className="text-center font-serif italic text-gold-300/50">
              {t('diary.no_results')}
            </p>
          )}

          <div className="space-y-6">
            {days.map((day) => (
              <section key={day}>
                <div className="divider-mystic mb-3">
                  <span className="text-[11px] uppercase tracking-widest text-gold-500/60">
                    {formatDate(day)}
                  </span>
                </div>

                <div className="space-y-3">
                  {grouped[day].map((r) => {
                    const isOpen = expanded === r.id;
                    const preview = r.interpretation?.slice(0, 120);
                    const hasMore = (r.interpretation?.length ?? 0) > 120;

                    return (
                      <OrnateFrame
                        key={r.id}
                        className="card-glow cursor-pointer transition-shadow hover:shadow-gold-500/20"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center
                                          rounded-full border border-gold-500/40 bg-night-900/60
                                          font-serif text-lg text-gold-400">
                            {KIND_ICON[r.kind] ?? '✦'}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="mb-1 text-[10px] uppercase tracking-widest text-gold-500/60">
                              {kindLabel(r.kind)}
                            </div>

                            {r.focus_question && (
                              <p className="mb-1 truncate text-xs italic text-gold-300/70">
                                «{r.focus_question}»
                              </p>
                            )}

                            <AnimatePresence initial={false} mode="wait">
                              {isOpen ? (
                                <motion.div
                                  key="full"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                >
                                  <RichText
                                    text={r.interpretation ?? ''}
                                    className="font-serif italic leading-relaxed text-sm text-gold-200"
                                  />
                                </motion.div>
                              ) : (
                                <motion.p
                                  key="preview"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  className="font-serif italic text-sm leading-relaxed text-gold-200"
                                >
                                  {/* Strip markdown markers in the preview */}
                                  {(preview ?? '').replace(/\*\*/g, '')}{hasMore ? '…' : ''}
                                </motion.p>
                              )}
                            </AnimatePresence>

                            <div className="mt-2 flex items-center gap-3">
                              {hasMore && (
                                <button
                                  className="text-[10px] uppercase tracking-widest
                                             text-gold-400/70 transition hover:text-gold-400"
                                  onClick={() => setExpanded(isOpen ? null : r.id)}
                                >
                                  {isOpen ? t('diary.collapse') : t('diary.expand')}
                                </button>
                              )}
                              <button
                                className="ml-auto text-[10px] uppercase tracking-widest
                                           text-rose-400/60 transition hover:text-rose-400"
                                onClick={() => setConfirmDelete(r.id)}
                                aria-label={t('diary.delete')}
                              >
                                🗑 {t('diary.delete')}
                              </button>
                            </div>
                          </div>
                        </div>
                      </OrnateFrame>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </>
      )}

      {/* ----- STATS TAB ----- */}
      {tab === 'stats' && !loading && stats && (
        <StatsPanel stats={stats} />
      )}

      {tab === 'stats' && !loading && stats && stats.total === 0 && (
        <OrnateFrame className="card-glow text-center">
          <p className="font-serif italic text-gold-300/60">{t('diary.stats_empty')}</p>
        </OrnateFrame>
      )}

      {/* Delete confirmation modal */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center
                       bg-night-950/80 backdrop-blur-sm px-6"
            onClick={() => !deleting && setConfirmDelete(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-sm rounded-3xl border border-rose-400/30 bg-night-800/95
                         px-6 py-6 text-center"
            >
              <p className="mb-1 text-3xl">🗑</p>
              <h3 className="mb-2 font-serif text-xl text-gold-200">
                {t('diary.delete_confirm_title')}
              </h3>
              <p className="mb-5 text-sm italic leading-relaxed text-gold-300/70">
                {t('diary.delete_confirm_body')}
              </p>
              {deleteErr && (
                <p className="mb-3 text-xs text-rose-400">{deleteErr}</p>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(null)}
                  disabled={deleting}
                  className="flex-1 rounded-lg border border-gold-500/30 py-2.5
                             text-sm text-gold-300/80 transition hover:border-gold-400
                             disabled:opacity-40"
                >
                  {t('diary.delete_confirm_no')}
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(confirmDelete)}
                  disabled={deleting}
                  className="flex-1 rounded-lg border border-rose-400/60
                             bg-rose-500/20 py-2.5 text-sm text-rose-200
                             transition hover:bg-rose-500/30 disabled:opacity-40"
                >
                  {deleting ? '…' : t('diary.delete_confirm_yes')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <PageNav />
    </main>
  );
}


// --- Stats panel components ---

function StatsPanel({ stats }: { stats: ReadingStats }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith('ru') ? 'ru' : 'en';

  if (stats.total === 0) return null;

  const kindsSorted = Object.entries(stats.by_kind).sort((a, b) => b[1] - a[1]);
  const maxKind = Math.max(...kindsSorted.map(([, c]) => c), 1);

  return (
    <div className="space-y-5">
      {/* Total */}
      <OrnateFrame className="card-glow text-center">
        <p className="text-[10px] uppercase tracking-widest text-gold-500/60">
          {t('diary.stats_total')}
        </p>
        <p className="mt-1 font-serif text-4xl text-gold-200">{stats.total}</p>
      </OrnateFrame>

      {/* Activity heatmap */}
      <OrnateFrame className="card-glow">
        <p className="mb-3 text-[10px] uppercase tracking-widest text-gold-500/60">
          {t('diary.stats_activity')}
        </p>
        <ActivityHeatmap byDate={stats.by_date} />
      </OrnateFrame>

      {/* By kind */}
      <OrnateFrame className="card-glow">
        <p className="mb-3 text-[10px] uppercase tracking-widest text-gold-500/60">
          {t('diary.stats_by_kind')}
        </p>
        <ul className="space-y-2">
          {kindsSorted.map(([k, count]) => (
            <li key={k}>
              <div className="mb-1 flex items-baseline justify-between text-xs">
                <span className="flex items-center gap-2 text-gold-300/80">
                  <span>{KIND_ICON[k] ?? '✦'}</span>
                  <span>{t(`diary.kind_${k}`, { defaultValue: k })}</span>
                </span>
                <span className="font-serif text-gold-200">{count}</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-night-900">
                <div
                  className="h-full bg-gradient-to-r from-gold-500/70 to-gold-300"
                  style={{ width: `${(count / maxKind) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      </OrnateFrame>

      {/* Top cards */}
      {stats.top_cards.length > 0 && (
        <OrnateFrame className="card-glow">
          <p className="mb-3 text-[10px] uppercase tracking-widest text-gold-500/60">
            {t('diary.stats_top_cards')}
          </p>
          <ul className="space-y-2.5">
            {stats.top_cards.map((c, i) => (
              <li
                key={c.name_en}
                className="flex items-center justify-between rounded-xl border
                           border-gold-500/15 bg-night-900/30 px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <span className="font-serif text-2xl text-gold-500/70 w-5 text-right">
                    {i + 1}
                  </span>
                  <span className="font-serif text-base text-gold-200">
                    {lang === 'ru' ? c.name_ru : c.name_en}
                  </span>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-gold-400/80">
                  {t('diary.card_drawn', { count: c.count })}
                </span>
              </li>
            ))}
          </ul>
        </OrnateFrame>
      )}
    </div>
  );
}


function ActivityHeatmap({ byDate }: { byDate: Record<string, number> }) {
  // 60-day grid, oldest → newest, left-to-right + wrap.
  const days = 60;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const cells = Array.from({ length: days }, (_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() - (days - 1 - i));
    const iso = d.toISOString().slice(0, 10);
    return { iso, count: byDate[iso] ?? 0 };
  });

  const max = Math.max(...cells.map((c) => c.count), 1);

  const intensity = (n: number): string => {
    if (n === 0) return 'bg-night-900/60 border border-gold-500/10';
    const ratio = n / max;
    if (ratio < 0.34) return 'bg-gold-500/25';
    if (ratio < 0.67) return 'bg-gold-500/55';
    return 'bg-gold-400';
  };

  return (
    <div className="grid grid-cols-10 gap-1">
      {cells.map((c) => (
        <div
          key={c.iso}
          title={`${c.iso}: ${c.count}`}
          className={`aspect-square rounded-sm ${intensity(c.count)}`}
        />
      ))}
    </div>
  );
}
